import { useMemo, useState } from 'react';
import { Button, Text } from '@fluentui/react-components';
import type { Preferences } from '../../../api/src/shared/contract';
import {
  likelySignatureImage,
  type MailAttachment,
} from '../../office/attachments';
import { formatBytes } from '../../lib/directory';
import { usePaneStyles } from '../styles';
import { LabeledField } from './LabeledField';

interface AttachmentListProps {
  attachments: MailAttachment[];
  preferences: Preferences;
  selected: Set<string>;
  onToggle: (id: string) => void;
}

export function AttachmentList({
  attachments,
  preferences,
  selected,
  onToggle,
}: AttachmentListProps) {
  const styles = usePaneStyles();
  const [showHidden, setShowHidden] = useState(false);

  const { visible, hidden } = useMemo(() => {
    const visible: MailAttachment[] = [];
    const hidden: MailAttachment[] = [];
    for (const att of attachments) {
      if (preferences.hideLikelySignatureImages && likelySignatureImage(att)) {
        hidden.push(att);
      } else {
        visible.push(att);
      }
    }
    return { visible, hidden };
  }, [attachments, preferences.hideLikelySignatureImages]);

  if (attachments.length === 0) {
    return (
      <LabeledField label="Attachments">
        <Text className={styles.hint}>No attachments on this message.</Text>
      </LabeledField>
    );
  }

  const shown = showHidden ? [...visible, ...hidden] : visible;
  const selectedCount = attachments.filter((a) => selected.has(a.id)).length;

  return (
    <LabeledField
      label="Attachments"
      meta={`${selectedCount} of ${attachments.length} selected`}
    >
      <div className={styles.rows}>
        {shown.map((att, index) => {
          const isLast = index === shown.length - 1 && (showHidden || hidden.length === 0);
          return (
            <label
              key={att.id}
              className={[styles.row, isLast ? styles.rowLast : ''].filter(Boolean).join(' ')}
            >
              <input
                type="checkbox"
                checked={selected.has(att.id)}
                onChange={() => onToggle(att.id)}
                style={{ flexShrink: 0 }}
              />
              <span className={styles.who}>
                <span className={styles.whoName}>{att.name}</span>
                <span className={styles.whoEmail}>
                  {formatBytes(att.size)}
                  {att.isInline ? ' · inline' : ''}
                  {likelySignatureImage(att) ? ' · likely signature' : ''}
                </span>
              </span>
            </label>
          );
        })}
        {hidden.length > 0 ? (
          <Button
            appearance="transparent"
            size="small"
            style={{ width: '100%', justifyContent: 'flex-start' }}
            onClick={() => setShowHidden((v) => !v)}
          >
            {showHidden
              ? `Hide ${hidden.length} likely signature image${hidden.length === 1 ? '' : 's'}`
              : `▸ ${hidden.length} image${hidden.length === 1 ? '' : 's'} likely from a signature (hidden)`}
          </Button>
        ) : null}
      </div>
      <Text className={styles.hint}>
        Inline images are unchecked by default. Checked ones upload and are replaced with a
        marker.
      </Text>
    </LabeledField>
  );
}

/** Initial selection based on preferences. */
export function defaultAttachmentSelection(
  attachments: MailAttachment[],
  preferences: Preferences,
): Set<string> {
  const selected = new Set<string>();
  for (const att of attachments) {
    if (preferences.hideLikelySignatureImages && likelySignatureImage(att)) continue;
    if (att.isInline) {
      if (preferences.inlineImagesCheckedByDefault) selected.add(att.id);
    } else if (preferences.attachmentsCheckedByDefault) {
      selected.add(att.id);
    }
  }
  return selected;
}
